import { type ReactNode } from "react";
import { AuthContext, type AuthValue } from "./AuthContext.js";
import { useFirebaseAuth } from "./useFirebaseAuth.js";
import { useE2EAuth } from "./useE2EAuth.js";

/**
 * Follows Admin Dashboard/src/auth/AuthProvider.tsx:21 — a context provider that picks a
 * strategy hook. The portal has one real strategy (no embed mode), so the branch collapses
 * to GCIP everywhere except under Playwright.
 */
const IS_E2E = import.meta.env.VITE_E2E === "1";

export function AuthProvider({ children }: { children: ReactNode }) {
  // Not a conditional hook: IS_E2E is a build-time constant, so exactly one of these is in
  // the bundle and the comparison is statically resolved. In a production build VITE_E2E is
  // undefined, the branch folds to `false`, and useE2EAuth is dropped entirely.
  const value = IS_E2E ? useE2EAuth() : useFirebaseAuth();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * The test seam, kept verbatim from AuthProvider.tsx:30. Every component test renders under
 * it; no test loads Firebase and no test needs a configured project.
 */
export function MockAuthProvider({
  children,
  value,
}: {
  children: ReactNode;
  value?: Partial<AuthValue>;
}) {
  const stub: AuthValue = {
    status: "authenticated",
    getToken: async () => "test-token",
    emailVerified: true,
    email: "ava@northwind.co",
    signUpWithPassword: async () => {},
    signInWithPassword: async () => true,
    signInWithGoogle: async () => {},
    sendVerificationEmail: async () => {},
    sendPasswordResetEmail: async () => {},
    reloadUser: async () => true,
    verifyPasswordResetCode: async () => "ava@northwind.co",
    confirmPasswordReset: async () => {},
    signOut: async () => {},
    ...value,
  };
  return <AuthContext.Provider value={stub}>{children}</AuthContext.Provider>;
}
